import { useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { Input } from '../UI/Input';
import { Select } from '../UI/Select';
import { Button } from '../UI/Button';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { REGIMEN_FISCAL_OPTIONS, USO_CFDI_OPTIONS, METODO_PAGO_OPTIONS, FORMA_PAGO_OPTIONS } from '../../types';

export function FiscalStep() {
  const { state, updateFormData, nextStep, prevStep, setError, clearError } = useWizard();
  const { fiscal } = state.formData;

  useEffect(() => {
    const errors: string[] = [];

    if (!fiscal?.rfc?.trim()) {
      errors.push('RFC es obligatorio.');
    } else {
      const rfc = fiscal.rfc.toUpperCase().trim();
      const rfcRegex = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/;
      if (!rfcRegex.test(rfc) || (rfc.length !== 12 && rfc.length !== 13)) {
        errors.push('RFC debe tener 12 o 13 caracteres alfanuméricos.');
      }
    }

    if (!fiscal?.razonSocial?.trim()) {
      errors.push('Razón Social es obligatoria.');
    }

    if (!fiscal?.regimenFiscal) {
      errors.push('Régimen Fiscal es obligatorio.');
    }

    if (!fiscal?.usoCfdi) {
      errors.push('Uso de CFDI es obligatorio.');
    }

    if (!fiscal?.codigoPostal?.trim()) {
      errors.push('Código Postal es obligatorio.');
    } else if (!/^\d{5}$/.test(fiscal.codigoPostal)) {
      errors.push('Código Postal debe tener 5 dígitos.');
    }

    if (!fiscal?.metodoPago) {
      errors.push('Método de Pago es obligatorio.');
    }

    if (!fiscal?.formaPago) {
      errors.push('Forma de Pago es obligatoria.');
    }

    if (errors.length > 0) {
      setError('fiscal', errors.join(' '));
    } else {
      clearError('fiscal');
    }
  }, [fiscal, setError, clearError]);

  const handleChange = (field: keyof typeof fiscal, value: string) => {
    updateFormData({
      fiscal: {
        ...fiscal,
        [field]: field === 'rfc' ? value.toUpperCase() : value,
      },
    });
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Datos fiscales
        </h2>
        <p className="text-gray-600">
          Proporciona los datos fiscales que aparecerán en tu factura.
        </p>
      </div>

      <div className="space-y-6">
        <Input
          label="RFC *"
          placeholder="Ej: XAXX010101000"
          value={fiscal?.rfc || ''}
          onChange={(e) => handleChange('rfc', e.target.value)}
          maxLength={13}
          required
        />

        <Input
          label="Razón Social *"
          placeholder="Nombre o razón social completa"
          value={fiscal?.razonSocial || ''}
          onChange={(e) => handleChange('razonSocial', e.target.value)}
          required
        />

        <Select
          label="Régimen Fiscal *"
          options={REGIMEN_FISCAL_OPTIONS}
          placeholder="Selecciona un régimen fiscal"
          value={fiscal?.regimenFiscal || ''}
          onChange={(e) => handleChange('regimenFiscal', e.target.value)}
          required
        />

        <Select
          label="Uso de CFDI *"
          options={USO_CFDI_OPTIONS}
          placeholder="Selecciona el uso de CFDI"
          value={fiscal?.usoCfdi || ''}
          onChange={(e) => handleChange('usoCfdi', e.target.value)}
          required
        />

        <Input
          label="Código Postal *"
          placeholder="Ej: 01000"
          value={fiscal?.codigoPostal || ''}
          onChange={(e) => handleChange('codigoPostal', e.target.value)}
          maxLength={5}
          pattern="\d{5}"
          required
        />

        <Select
          label="Método de Pago *"
          options={METODO_PAGO_OPTIONS}
          placeholder="Selecciona método de pago"
          value={fiscal?.metodoPago || ''}
          onChange={(e) => handleChange('metodoPago', e.target.value)}
          required
        />

        <Select
          label="Forma de Pago *"
          options={FORMA_PAGO_OPTIONS}
          placeholder="Selecciona forma de pago"
          value={fiscal?.formaPago || ''}
          onChange={(e) => handleChange('formaPago', e.target.value)}
          required
        />

        <Input
          label="Correo para recibir factura (opcional)"
          type="email"
          placeholder="facturacion@correo.com"
          value={fiscal?.emailFacturacion || ''}
          onChange={(e) => handleChange('emailFacturacion', e.target.value)}
          helper="Si no se llena, se usará el correo de contacto"
        />
      </div>

      <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200">
        <Button
          onClick={prevStep}
          variant="ghost"
          icon={ArrowLeft}
        >
          Atrás
        </Button>
        <Button
          onClick={nextStep}
          icon={ArrowRight}
          disabled={!!state.errors.fiscal}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}